import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { CompanyTodoCardData } from "models/propTypes/allCompaniesPropTypes";
import { AllCompaniesConstants } from "utils/constants";
//#endregion
import { useAllCompaniesTodoService } from "serviceHooks";
import {
  getCachedCompanyTodoCard,
  getCompanyTodoCardsRefreshedAt,
  markCompanyTodoCardsRefreshed,
  setCachedCompanyTodoCard,
  toCompanyTodoCard,
} from "./companyTodoCards";

/**
 * Loads a To-do summary for every company the user has access to, a few at a time, and publishes each
 * card as soon as its summary lands. Previously loaded counts are shown until the fresh ones arrive, and
 * a company whose summary fails keeps its previous counts or, without any, is marked as failed.
 *
 * Opening the overview again within the cooldown reuses the cached cards instead of loading; the
 * reload button always loads.
 */
function useAllCompaniesTodos() {
  const companies = useSelector((store: AppState) => store.auth.companies);
  const personId = useSelector((store: AppState) => store.auth.loggedInUser.personId);

  const allCompaniesTodoService = useAllCompaniesTodoService();

  const [cards, setCards] = React.useState<CompanyTodoCardData[]>([]);
  const [refreshedAt, setRefreshedAt] = React.useState<Date | undefined>(getCompanyTodoCardsRefreshedAt);
  const [refreshing, setRefreshing] = React.useState<boolean>(false);
  // Bumped on every load so a run that was superseded (unmount, reload) stops publishing.
  const runIdRef = React.useRef(0);

  const getCards = React.useCallback(
    () =>
      companies?.map(
        (company) => getCachedCompanyTodoCard(personId, company.id) ?? toCompanyTodoCard(company, "loading")
      ) ?? [],
    [companies, personId]
  );

  const load = React.useCallback(async () => {
    if (!companies) return;

    const runId = ++runIdRef.current;
    const isCurrentRun = () => runId === runIdRef.current;

    setCards(getCards());
    setRefreshing(true);

    const publish = (card: CompanyTodoCardData) => {
      if (!isCurrentRun()) return;
      setCards((current) => current.map((existing) => (existing.companyId === card.companyId ? card : existing)));
    };

    let nextIndex = 0;
    const worker = async () => {
      while (isCurrentRun() && nextIndex < companies.length) {
        const company = companies[nextIndex++];
        try {
          const summary = await allCompaniesTodoService.getTodoSummary(company.id);
          // A superseded run must not overwrite counts a newer load or the dashboard sync has cached.
          if (!isCurrentRun()) return;
          const card = toCompanyTodoCard(company, "loaded", summary);
          setCachedCompanyTodoCard(personId, card);
          publish(card);
        } catch {
          publish(getCachedCompanyTodoCard(personId, company.id) ?? toCompanyTodoCard(company, "failed"));
        }
      }
    };

    const workerCount = Math.min(AllCompaniesConstants.SummaryConcurrency, companies.length);
    await Promise.all(Array.from({ length: workerCount }, () => worker()));

    if (!isCurrentRun()) return;
    const now = new Date();
    markCompanyTodoCardsRefreshed(now);
    setRefreshedAt(now);
    setRefreshing(false);
  }, [companies, personId, getCards]);

  React.useEffect(() => {
    const lastRefreshedAt = getCompanyTodoCardsRefreshedAt();
    const withinCooldown =
      lastRefreshedAt !== undefined && Date.now() - lastRefreshedAt.getTime() < AllCompaniesConstants.RefreshCooldownMs;
    const allCached = getCards().every((card) => card.state !== "loading");

    if (withinCooldown && allCached) {
      setCards(getCards());
      // A run superseded mid-refresh never clears its own flag.
      setRefreshing(false);
    } else void load();

    return () => {
      runIdRef.current++;
    };
  }, [load, getCards]);

  return {
    cards,
    loading: cards.some((card) => card.state === "loading"),
    refreshing,
    refreshedAt,
    reload: load,
  };
}

export default useAllCompaniesTodos;
